import React, { useState, useEffect, useCallback } from "react";
import { Switch, Redirect, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import clsx from "clsx";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import ToolsIcon from "@material-ui/icons/BuildOutlined";
import AgentIcon from "@material-ui/icons/RecordVoiceOverOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import MoveIcon from "@material-ui/icons/FolderOutlined";
import CallInIcon from "@material-ui/icons/PhoneCallbackOutlined";
import { format } from "date-fns";
import { toast } from "react-toastify";

import Route from "../../routes/Route";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { formatPhone } from "../../utils/formatPhone";
import ToolsList from "./ToolsList";
import AgentEditor from "./AgentEditor";
import AgentHero from "./AgentHero";

const PROVIDER_LABEL = { openai: "OpenAI", gptlive: "GPT-Live", grok: "Grok", eleven: "ElevenLabs", gemini: "Gemini", nova: "Amazon Nova" };

// persona padrão pra um agente novo — dá um ponto de partida bem estruturado
// (o usuário edita depois). Baseada num modelo de agente de suporte.
const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1000, margin: "0 auto", padding: theme.spacing(4, 3.5, 6) },
  header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2.5) },
  headText: { flex: 1, minWidth: 0 },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 24, letterSpacing: "-0.01em", color: theme.app.ink, margin: 0 },
  subtitle: { color: theme.app.textSecondary, fontSize: 13.5, marginTop: 4, maxWidth: 560 },
  toolsBtn: { flexShrink: 0, textTransform: "none", fontSize: 13, fontWeight: 600, color: theme.app.ink, background: "#fff", border: `1px solid ${theme.app.borderInput}`, borderRadius: 8, padding: "8px 16px", boxShadow: "none", "& svg": { fontSize: 17 }, "&:hover": { background: theme.app.hover, boxShadow: "none" } },
  primaryBtn: { flexShrink: 0, textTransform: "none", fontSize: 13, fontWeight: 600, color: "#fff", background: theme.app.dark, borderRadius: 8, padding: "9px 18px", boxShadow: "none", "&:hover": { background: theme.app.ink, boxShadow: "none" }, "&.Mui-disabled": { background: theme.app.border, color: theme.app.textTertiary } },

  search: { width: "100%", marginBottom: theme.spacing(2) },
  searchInput: { borderRadius: 999, background: "#fff" },

  list: { display: "flex", flexDirection: "column", gap: 10 },
  card: {
    display: "flex", alignItems: "center", gap: 16, background: "#fff", border: `1px solid ${theme.app.border}`,
    borderRadius: 16, padding: "14px 18px", cursor: "pointer", transition: "box-shadow .15s ease, border-color .15s ease",
    "&:hover": { boxShadow: "0 4px 16px rgba(27,34,42,.06)", borderColor: theme.app.borderInput },
    "&:hover $kebab": { opacity: 1 },
  },
  avatar: { width: 42, height: 42, borderRadius: 999, flexShrink: 0, background: "#EFF8D6", color: "#5f7a12", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 21 } },
  main: { flex: 1, minWidth: 0 },
  name: { fontSize: 14.5, fontWeight: 600, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sub: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  // número de entrada que aciona o agente (chamada recebida)
  phoneRow: { display: "inline-flex", alignItems: "center", gap: 6, marginTop: 7, padding: "3px 9px 3px 7px", borderRadius: 999, background: "#EFF8D6", color: "#5f7a12", "& svg": { fontSize: 14 } },
  phoneNum: { fontFamily: theme.app.mono, fontSize: 12, fontWeight: 600, letterSpacing: "-0.01em" },
  phoneMore: { fontFamily: theme.app.mono, fontSize: 11.5, fontWeight: 600, opacity: 0.8 },
  updated: { fontSize: 12, color: theme.app.textTertiary, flexShrink: 0, marginRight: 4 },
  kebab: { color: theme.app.textTertiary, opacity: 0.55, transition: "opacity .12s", "&:hover": { color: theme.app.ink } },

  // nome + selo
  nameRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  draftPill: {
    display: "inline-flex", alignItems: "center", flexShrink: 0,
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
    color: "#8A6D1F", background: "#FBF3DC", border: "1px solid #EBDDB0", borderRadius: 999, padding: "1px 8px",
  },

  // skeleton de carregamento
  "@keyframes agShimmer": { "0%": { backgroundPosition: "-320px 0" }, "100%": { backgroundPosition: "320px 0" } },
  skCard: { cursor: "default", "&:hover": { boxShadow: "none", borderColor: theme.app.border } },
  skBox: {
    background: `linear-gradient(90deg, ${theme.app.lineSoft || "#EEF0F3"} 25%, ${theme.app.hover || "#F7F8FA"} 50%, ${theme.app.lineSoft || "#EEF0F3"} 75%)`,
    backgroundSize: "640px 100%", animation: "$agShimmer 1.3s ease-in-out infinite", borderRadius: 6,
  },
  skAvatar: { width: 42, height: 42, borderRadius: 999, flexShrink: 0 },
  skLineLg: { height: 13, width: "42%", borderRadius: 6 },
  skLineSm: { height: 10, width: "26%", borderRadius: 5, marginTop: 9 },
  menuPaper: { minWidth: 180, borderRadius: 16, border: `1px solid ${theme.app.border}`, boxShadow: "0 12px 30px rgba(27,34,42,.14)", marginTop: 6 },
  menuItem: { fontSize: 13, gap: 10, padding: "8px 12px", "& svg": { fontSize: 18, color: theme.app.textTertiary } },
  menuDanger: { color: theme.app.danger || "#C0453B", "& svg": { color: theme.app.danger || "#C0453B" } },

  empty: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 16, padding: theme.spacing(7, 3), textAlign: "center" },
  emptyIcon: { width: 46, height: 46, borderRadius: 999, background: theme.app.lineSoft, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12, "& svg": { fontSize: 22, color: theme.app.textTertiary } },
  emptyText: { color: theme.app.textSecondary, fontSize: 13.5 },
}));

// Lista de agentes (cards na tela — antes ficava no menu lateral)
const AgentsList = () => {
  const classes = useStyles();
  const history = useHistory();
  const { activeProjectId, projects } = useProject();
  const { confirm } = useConfirm();
  const [agents, setAgents] = useState([]);
  const [numbersByAgent, setNumbersByAgent] = useState({}); // agentId → números de entrada
  const [numbersReady, setNumbersReady] = useState(false); // já sabemos quem tem número (p/ o selo "rascunho")
  const [loading, setLoading] = useState(true); // skeleton no 1º carregamento
  const [search, setSearch] = useState("");
  const [menu, setMenu] = useState(null);
  const [moveAgent, setMoveAgent] = useState(null); // agente sendo movido de projeto
  const [moveTarget, setMoveTarget] = useState(""); // projeto de destino escolhido
  const [moving, setMoving] = useState(false);

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/applications", { params: { projectId: activeProjectId } });
      setAgents((data.applications || []).filter(a => a.triggerType === "agent"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agents/numbers");
        setNumbersByAgent(data.byAgent || {});
      } catch (e) {
        /* silencioso — o card só deixa de mostrar o número */
      } finally {
        setNumbersReady(true);
      }
    })();
  }, [agents]);

  // "Novo agente" → tela-porta (hero), estilo nova conversa: descreva/escolha um
  // exemplo e o copiloto monta o agente na sua frente.
  const newAgent = () => history.push("/agents/new");

  const remove = async a => {
    setMenu(null);
    const ok = await confirm({ title: "Excluir agente", message: `Excluir o agente "${a.name}"?`, confirmText: "Excluir", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/applications/${a.id}`);
      setAgents(list => list.filter(x => x.id !== a.id));
    } catch (err) {
      toastError(err);
    }
  };

  // Mover agente para outro projeto. Abre o modal com os projetos do dono (menos o atual).
  const openMove = a => {
    setMenu(null);
    setMoveTarget("");
    setMoveAgent(a);
  };
  const doMove = async () => {
    if (!moveAgent || !moveTarget) return;
    setMoving(true);
    try {
      await api.put(`/applications/${moveAgent.id}/project`, { projectId: Number(moveTarget) });
      // saiu do projeto ativo → some desta lista
      setAgents(list => list.filter(x => x.id !== moveAgent.id));
      const dest = projects.find(p => String(p.id) === String(moveTarget));
      toast.success(`Agente movido para "${dest ? dest.name : "o projeto"}".`);
      setMoveAgent(null);
    } catch (err) {
      toastError(err);
    } finally {
      setMoving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = agents.filter(a => !q || (a.name || "").toLowerCase().includes(q));

  const subLabel = a => {
    const c = a.triggerConfig || {};
    const prov = PROVIDER_LABEL[c.provider] || "Agente de voz";
    return c.voice ? `${prov} · ${c.voice}` : prov;
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div className={classes.headText}>
            <h1 className={classes.title}>Agentes de IA</h1>
            <div className={classes.subtitle}>Agentes de voz (persona + voz + ferramentas) que atendem suas ligações.</div>
          </div>
          <Button className={classes.toolsBtn} startIcon={<ToolsIcon />} onClick={() => history.push("/agents/tools")}>
            Ferramentas
          </Button>
          <Button className={classes.primaryBtn} startIcon={<AddIcon />} onClick={newAgent} disabled={!activeProjectId}>
            Novo agente
          </Button>
        </div>

        <TextField
          className={classes.search}
          placeholder="Buscar agente…"
          variant="outlined"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ className: classes.searchInput, startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />

        {loading ? (
          <div className={classes.list}>
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className={clsx(classes.card, classes.skCard)}>
                <div className={clsx(classes.avatar, classes.skBox, classes.skAvatar)} />
                <div className={classes.main}>
                  <div className={clsx(classes.skBox, classes.skLineLg)} />
                  <div className={clsx(classes.skBox, classes.skLineSm)} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className={classes.empty}>
            <div className={classes.emptyIcon}><AgentIcon /></div>
            <div className={classes.emptyText}>
              {agents.length === 0 ? 'Nenhum agente ainda. Clique em "Novo agente" para criar o primeiro.' : "Nenhum agente com esse nome."}
            </div>
          </div>
        ) : (
          <div className={classes.list}>
            {filtered.map(a => {
              const isDraft = numbersReady && (numbersByAgent[a.id] || []).length === 0;
              return (
              <div key={a.id} className={classes.card} onClick={() => history.push(`/agents/${a.id}`)}>
                <div className={classes.avatar}><AgentIcon /></div>
                <div className={classes.main}>
                  <div className={classes.nameRow}>
                    <span className={classes.name}>{a.name}</span>
                    {isDraft && <span className={classes.draftPill}>Rascunho</span>}
                  </div>
                  <div className={classes.sub}>{subLabel(a)}</div>
                  {(numbersByAgent[a.id] || []).length > 0 && (
                    <div className={classes.phoneRow} title="Número que recebe chamadas para este agente">
                      <CallInIcon />
                      <span className={classes.phoneNum}>{formatPhone(numbersByAgent[a.id][0].number)}</span>
                      {numbersByAgent[a.id].length > 1 && (
                        <span className={classes.phoneMore}>+{numbersByAgent[a.id].length - 1}</span>
                      )}
                    </div>
                  )}
                </div>
                {a.updatedAt && <div className={classes.updated}>{format(new Date(a.updatedAt), "dd/MM/yyyy")}</div>}
                <span onClick={e => e.stopPropagation()} style={{ display: "flex" }}>
                  <IconButton size="small" className={classes.kebab} onClick={e => setMenu({ anchor: e.currentTarget, agent: a })}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </span>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <Menu
        anchorEl={menu && menu.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <MenuItem className={classes.menuItem} onClick={() => openMove(menu.agent)}>
          <MoveIcon fontSize="small" />
          Mover para outro projeto
        </MenuItem>
        <MenuItem className={clsx(classes.menuItem, classes.menuDanger)} onClick={() => remove(menu.agent)}>
          <DeleteIcon fontSize="small" />
          Excluir agente
        </MenuItem>
      </Menu>

      <Dialog open={!!moveAgent} onClose={() => (moving ? undefined : setMoveAgent(null))} maxWidth="xs" fullWidth PaperProps={{ className: classes.menuPaper }}>
        <DialogTitle>Mover agente para outro projeto</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Projeto de destino"
            fullWidth
            variant="outlined"
            size="small"
            value={moveTarget}
            onChange={e => setMoveTarget(e.target.value)}
          >
            {projects
              .filter(p => String(p.id) !== String(activeProjectId))
              .map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveAgent(null)} disabled={moving}>Cancelar</Button>
          <Button color="primary" variant="contained" disableElevation onClick={doMove} disabled={moving || !moveTarget}>
            {moving ? "Movendo…" : "Mover"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const Agents = () => (
  <Switch>
    <Route exact path="/agents/tools" component={ToolsList} isPrivate />
    <Route exact path="/agents/new" component={AgentHero} isPrivate />
    <Route exact path="/agents/:agentId" component={AgentEditor} isPrivate />
    <Route exact path="/agents" component={AgentsList} isPrivate />
    <Redirect to="/agents" />
  </Switch>
);

export default Agents;
